import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Paper from 'apollo-react/components/Paper';
import Typography from 'apollo-react/components/Typography';
import Grid from 'apollo-react/components/Grid';
import Box from 'apollo-react/components/Box';import CircularProgress from 'apollo-react/components/CircularProgress';
import axiosInstance from '../../../axiosInstance';
import ViewOnlyFileDisplay from '../../common/ViewOnlyFileDisplay';

const ManufacturerSiteDetails = ({ persona }) => {
  const { id } = useParams();
  const [manufacturerSite, setManufacturerSite] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchManufacturerSite = useCallback(async () => {
    setLoading(true);
    try {
      // admin users see all manufacturer sites
      const response = await axiosInstance.get(`/api/manufacturers-sites/${id}`);
      setManufacturerSite(response.data);
    } catch (error) {
      console.error('error fetching manufacturer site:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchManufacturerSite();
  }, [fetchManufacturerSite]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!manufacturerSite) {
    return (
      <Paper elevation={1} sx={{ padding: 4, margin: 0 }}>
        <Typography variant="body1">No manufacturer site details available.</Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ padding: 3 }}>
      <Paper elevation={1} sx={{ padding: 4, margin: 0 }}>
        {/* header with back button */}
        <Box sx={{ marginBottom: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography
            variant="h1"
            sx={{ fontWeight: 600 }}
          >
            Manufacturer Site Details
          </Typography>
        </Box>

        {/* request information */}
        {manufacturerSite.request && (
          <Box sx={{ marginBottom: 4 }}>
            <Typography
              variant="title1"
              component="h2"
              sx={{
                fontWeight: 500,
                marginBottom: 3,
                color: "#333",
                paddingBottom: 1,
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              Request Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="textSecondary">Reference Number</Typography>
                <Typography variant="body1">{manufacturerSite.request.reference_number || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="textSecondary">Status</Typography>
                <Typography variant="body1">{manufacturerSite.requestProgressStatus || 'Active'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="textSecondary">Applicant Name</Typography>
                <Typography variant="body1">{manufacturerSite.end_user?.username || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="textSecondary">Request Name</Typography>
                <Typography variant="body1">{manufacturerSite.request.study_name || '-'}</Typography>
              </Grid>
              {manufacturerSite.request.end_user && (
                <>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="textSecondary">Submitted By</Typography>
                    <Typography variant="body1">{manufacturerSite.request.end_user.username || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="textSecondary">Email</Typography>
                    <Typography variant="body1">{manufacturerSite.request.end_user.email || '-'}</Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        )}

        {/* manufacturer information section */}
        <Box sx={{ marginBottom: 4 }}>
          <Typography
            variant="title1"
            component="h2"
            sx={{
              fontWeight: 500,
              marginBottom: 3,
              color: "#333",
              paddingBottom: 1,
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            Manufacturer Information
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">Manufacturer License</Typography>
              <Typography variant="body1">{manufacturerSite.manufacturer_license || '-'}</Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">Manufacturer Name</Typography>
              <Typography variant="body1">{manufacturerSite.manufacturer_name || '-'}</Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">Selected Manufacturer</Typography>
              <Typography variant="body1">{manufacturerSite.selectedManufacturerDetails?.manufacturer_name || '-'}</Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">Activity</Typography>
              <Typography variant="body1">{manufacturerSite.activity || '-'}</Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">Active Substance</Typography>
              <Typography variant="body1">{manufacturerSite.active_substance || '-'}</Typography>
            </Grid>
          </Grid>
        </Box>

        {/* site information section */}
        <Box sx={{ marginBottom: 4 }}>
          <Typography
            variant="title1"
            component="h2"
            sx={{
              fontWeight: 500,
              marginBottom: 3,
              color: "#333",
              paddingBottom: 1,
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            Site Information
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">Manufacturing Site Address</Typography>
              <Typography variant="body1">{manufacturerSite.manufacturers_site_address || '-'}</Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">Manufacturing Process Details</Typography>
              <Typography variant="body1">{manufacturerSite.manufacturing_process_details || '-'}</Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">Quality Control Information</Typography>
              <Typography variant="body1">{manufacturerSite.quality_control_information || '-'}</Typography>
            </Grid>
          </Grid>
        </Box>

        {/* contact information section */}
        <Box sx={{ marginBottom: 4 }}>
          <Typography
            variant="title1"
            component="h2"
            sx={{
              fontWeight: 500,
              marginBottom: 3,
              color: "#333",
              paddingBottom: 1,
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            Contact Information
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">Contact Person Name</Typography>
              <Typography variant="body1">{manufacturerSite.contact_person_name || '-'}</Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">Email</Typography>
              <Typography variant="body1">{manufacturerSite.email || '-'}</Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">Phone</Typography>
              <Typography variant="body1">{manufacturerSite.phone || '-'}</Typography>
            </Grid>
          </Grid>
        </Box>

        {/* documents section */}
        <Box sx={{ marginBottom: 4 }}>
          <Typography
            variant="title1"
            component="h2"
            sx={{
              fontWeight: 500,
              marginBottom: 3,
              color: "#333",
              paddingBottom: 1,
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            Documents
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box sx={{ marginBottom: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#555', marginBottom: 1 }}>
                  GMP Certificate
                </Typography>
                {manufacturerSite.gmpCertificateFile ? (
                  <ViewOnlyFileDisplay
                    files={[{
                      id: manufacturerSite.gmpCertificateFile.id,
                      file_id: manufacturerSite.gmpCertificateFile.id,
                      name: manufacturerSite.gmpCertificateFile.original_name,
                      file_name: manufacturerSite.gmpCertificateFile.original_name,
                      file_size: manufacturerSite.gmpCertificateFile.file_size,
                      mime_type: manufacturerSite.gmpCertificateFile.mime_type
                    }]}
                    label=""
                    helperText="download gmp certificate"
                    persona={persona}
                  />
                ) : (
                  <Typography variant="body1" sx={{ color: '#999', fontStyle: 'italic' }}>
                    Not provided
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* timestamps */}
        <Box sx={{ marginTop: 4, paddingTop: 3, borderTop: "1px solid #e0e0e0" }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">Created At</Typography>
              <Typography variant="body1">{new Date(manufacturerSite.createdAt).toLocaleString('en-GB')}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">Last Updated</Typography>
              <Typography variant="body1">{new Date(manufacturerSite.updatedAt).toLocaleString('en-GB')}</Typography>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default ManufacturerSiteDetails;

